<template>
  <component
    :is="card ? AppCard : 'section'"
    :ref="setRoot"
    :as="card ? 'section' : undefined"
    class="state"
    :class="[toneClass, { inline, flow, card }]"
  >
    <span v-if="glyph" class="circle" aria-hidden="true">
      <component :is="glyph" class="glyph" />
    </span>
    <!-- An alert holds the words only: a card read out with its buttons would say «Try again»
         as if it were the news. Anything polite goes to the app's live region instead. -->
    <div class="message" :role="role">
      <h2 class="title">{{ title }}</h2>
      <p v-if="body" class="body">{{ body }}</p>
    </div>
    <div v-if="$slots.default" class="extra">
      <slot />
    </div>
    <!-- A screen-wide error draws its buttons in the screen's strip, where every screen has its
         main action (К-1, Ф-15), and a sheet's full state in the sheet's footer (MOL-211); the login
         at the bottom of its own. -->
    <Teleport
      v-if="kind === 'error' || $slots.action"
      :to="dockTarget"
      :disabled="!dockTarget"
      defer
    >
      <div ref="actions" class="action" :class="{ docked: dockTarget }">
        <!-- A version waits: the error may well be the old code reading the new server's answer,
             and the reload loses nothing (MOL-132, В-2). -->
        <!-- In the flow a button is as wide as its word, not its place: not a block there, so it holds
             «Обновить» and «Обновляем…» in one cell and «Повторить» beside it stays put (MOL-225,
             adversarial А10 of MOL-208). -->
        <AppButton
          v-if="kind === 'error' && updating && !card"
          class="reload"
          :block="!flow"
          :size="dockTarget ? 'large' : 'regular'"
          :busy="applying"
          :busy-label="t('update.applying')"
          @click="update.apply()"
        >
          <template #icon><IconUpdate /></template>
          {{ t('update.apply') }}
        </AppButton>
        <AppButton
          v-if="kind === 'error'"
          :block="!card"
          :size="dockTarget && !updating ? 'large' : 'regular'"
          :variant="card ? 'ghost' : updating ? 'secondary' : 'primary'"
          ref="retryButton"
          :class="{ word: card }"
          :busy="retrying"
          :busy-label="t('state.retrying')"
          @click="$emit('retry')"
        >
          <template #icon><IconRefresh /></template>
          {{ t('state.retry') }}
        </AppButton>
        <slot name="action" />
        <AppButton v-if="reportable" variant="ghost" block aria-haspopup="dialog" @click="report">
          <template #icon><IconReport /></template>
          {{ t('state.report') }}
        </AppButton>
      </div>
    </Teleport>
  </component>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  watchEffect,
  type Component,
  type ComponentPublicInstance,
  type PropType,
} from 'vue'
import { getActivePinia } from 'pinia'
import { useI18n } from 'vue-i18n'
import IconAlert from '~icons/mdi/alert-circle-outline'
import IconAttention from '~icons/mdi/alert-outline'
import IconCloudOff from '~icons/mdi/cloud-off-outline'
import IconReport from '~icons/mdi/message-alert-outline'
import IconRefresh from '~icons/mdi/refresh'
import IconUpdate from '~icons/mdi/update'
import { lastRefusal } from '@/api'
import AppButton from '@/components/AppButton.vue'
import AppCard from '@/components/AppCard.vue'
import { useAnnouncer } from '@/composables/useAnnouncer'
import { sayAnswerWord } from '@/composables/useAnswerWord'
import { useStateStrip } from '@/composables/useStateStrip'
import { useUpdateAnnouncement } from '@/composables/useUpdateAnnouncement'
import { usePwaUpdate } from '@/pwaUpdate'
import { useActorStore } from '@/stores/actor'
import { useFeedbackSheetStore } from '@/stores/feedbackSheet'
import { focusTitleOf } from '@/transitions'

export type StateKind = 'empty' | 'error' | 'offline' | 'attention'

/**
 * The tones a screen may choose — for offline alone. `bad` is not one of them: it belongs to
 * `error` alone and is never asked for, so `tone="bad"` does not type-check anywhere — offline
 * cannot be drawn red.
 */
export type StateTone = 'good' | 'warn'

// Which tones each kind may take. Only offline has a choice; empty is one quiet form whatever it
// says (Ф-16, К-8), and error and attention are fixed.
const TONES: Record<StateKind, readonly StateTone[]> = {
  empty: [],
  offline: ['good', 'warn'],
  error: [],
  attention: [],
}

// What offline is drawn in when it was given no tone it takes: yellow, «the data may be old»,
// which is true of every offline screen; green is a promise.
const FALLBACK: StateTone = 'warn'

// Own keys only: `in` walks the prototype, and «toString» would pass for a kind.
function isKind(value: unknown): value is StateKind {
  return typeof value === 'string' && Object.hasOwn(TONES, value)
}

/**
 * What the types cannot say, since a prop's type does not depend on another prop's value:
 * an empty state brings its own icon and the others are drawn with theirs; a tone is given
 * exactly when the kind has a choice, and it is one of that kind's.
 *
 * Every empty state has its circle, on `--surface-2` (Ф-16): MOL-77 took the circle away from
 * three of them because a terracotta one over an action read as a button and was tapped; a
 * circle of the page's own quiet fill does not, and one form is what the eye learns.
 */
function fits(kind: unknown, props: Record<string, unknown>): boolean {
  if (!isKind(kind)) return false
  const allowed = TONES[kind]
  const tone = props.tone as StateTone | undefined
  const toneFits =
    allowed.length === 0 ? tone === undefined : tone !== undefined && allowed.includes(tone)
  const iconFits = (kind === 'empty') === (props.icon !== undefined)
  return toneFits && iconFits
}

/**
 * Every state of every screen that is not loading: empty, error, offline — and «attention»,
 * the device-identity notice that fits none of them. Twelve cards of the mockup are twelve
 * sets of props, not twelve components.
 *
 * Four kinds, told apart by form and confirmed by colour (41 v2, Ф-35): empty is a quiet circle
 * with the screen's own icon, error a red one with a ring, attention a yellow triangle, offline
 * the cloud. Offline is green or yellow and never red — the connection drops at the shelf all
 * the time, and an app that panics every time teaches people to ignore it.
 *
 * An error always offers «Try again», drawn here and reported as `retry`: twelve copies of one
 * word would drift apart. Every other action is the screen's own and comes through `action`,
 * after the retry where there is one — the search's «Take from recent». While a new version of the
 * app waits, the error offers it first, «Обновить», and the retry second (MOL-132).
 *
 * An error of the whole screen draws those buttons in the screen's strip (`useStateStrip`, К-1,
 * Ф-15): «Повторить» stood at the bottom of the free height — on each screen at its own — and over
 * a strip with a live action it was a second filled button. The strip is then the error's alone:
 * the screen's own action and «Вышла новая версия» step aside, so «Обновить» is offered once (8c).
 * The login has no strip, and keeps them at the bottom of the block. A sheet's strip is its footer
 * (MOL-211): there any full state with an action of its own holds it as well — the scanner's refusals.
 *
 * Last, quieter than both, «Сообщить о проблеме» (MOL-147, Р-5): the sheet «Написать разработчику»
 * on «Сломалось», with the code of the API's last refusal before the error was shown (В-1). Only where the screen as a
 * whole failed — not a section's `inline` error (сверка С-1) — only for somebody known, which
 * leaves out the login, and never inside a sheet, since a sheet over a sheet the history does not
 * hold. Drawn here, so no screen has to remember it.
 *
 * Texts arrive translated, never as a key prefix: a key assembled from a string is invisible
 * to the linter and to vue-tsc alike (MOL-16, О-12).
 *
 * Laid out to take the free height of the screen with an action of its own at the bottom, under
 * the thumb; `inline` keeps it to its own height, for a notice above the content — the surface
 * around it is the card's, not this block's. An `inline` error is that card itself, the quiet one
 * (К-13, 77 v2 2c): «Повторить» a ghost the width of its word, and no «Обновить», which the strip's
 * own row already offers while the screen works.
 *
 * `flow` is for a screen whose field holds the keyboard — «Что взяли?» (owner's В-20 «а»): its states
 * come while the person types, and the strip at the bottom is under the keys on an iPhone. The block
 * keeps its own height and its buttons right under the words, and takes no strip; an error stays the
 * whole screen's — its circle, «Повторить» filled, «Сообщить о проблеме», an alert — not the quiet card.
 */
export default defineComponent({
  name: 'ScreenState',
  components: { AppButton, IconRefresh, IconReport, IconUpdate },
  props: {
    kind: { type: String as PropType<StateKind>, required: true, validator: fits },
    tone: { type: String as PropType<StateTone | undefined>, default: undefined },
    icon: { type: [Object, Function] as PropType<Component>, default: undefined },
    title: { type: String, required: true },
    body: { type: String, default: undefined },
    inline: { type: Boolean, default: false },
    /** The buttons under the words, never in the strip: a screen typed in (В-20). */
    flow: { type: Boolean, default: false },
    /**
     * Its words out of the live region while it stands: an answer dimmed while the next is on its way
     * is for the text before, and is not read out (the search's rule, MOL-208 adversarial А3).
     */
    silent: { type: Boolean, default: false },
    /** «Повторить» at work, the block standing meanwhile: «Повторяем…» in its place (MOL-225, MOL-349). */
    retrying: { type: Boolean, default: false },
    /**
     * Which failed answer the block stands for, where a retry leaves it standing: a new one says the same
     * words again (C1, as `StatusStrip`'s) — the sign-in's card, asked again quietly (MOL-349, adversarial В2).
     */
    attempt: { type: Number, default: 0 },
  },
  emits: ['retry'],
  setup(props) {
    const { t } = useI18n()
    const root = ref<HTMLElement | null>(null)
    // The root is a card's component for a section's error, a plain element otherwise.
    function setRoot(el: Element | ComponentPublicInstance | null): void {
      const node = el instanceof Element ? el : (el?.$el as unknown)
      root.value = node instanceof HTMLElement ? node : null
    }
    const actions = ref<HTMLElement | null>(null)
    const retryButton = ref<ComponentPublicInstance | null>(null)
    const announce = useAnnouncer()
    // No connection or an error: what became of the answer, said by the screen itself (MOL-349).
    sayAnswerWord(() => props.kind === 'offline' || props.kind === 'error')

    // The strip is asked for while the block is an error of the whole screen, and let go as it
    // stops being one; the first to ask holds it, and the next takes it once that one lets go.
    const strip = useStateStrip()
    const me = Symbol('screen-state')
    // In a sheet a state with buttons of its own holds the footer too — the scanner's «Нет доступа к
    // камере» (MOL-211). Whether it has any is read off its block of actions: a button there that is shown.
    // Not off the slot, which is no reactive source — a button come later or gone with the kind unchanged
    // held the footer only from the next change of kind (adversarial А1, А2) — nor off an element drawn: a
    // wrapper whose button stands under its `v-if`, or a button under `v-show`, held an empty footer over
    // the sheet's «Сохранить» (round 2, А3; round 3, Г). And as the block's DOM changes, not as the block
    // renders: a component in the slot redraws itself without it (round 3, Д) — and as the styles do.
    // Watched in a sheet only.
    const acting = ref(false)
    function measureActing(): void {
      const block = actions.value
      acting.value = [...(block?.querySelectorAll('button') ?? [])].some((button) => {
        for (let node: Element | null = button; node && node !== block; node = node.parentElement) {
          // `hidden` and a class that hides are a `display: none` too, as the browser computes it.
          if (getComputedStyle(node).display === 'none') return false
        }
        return true
      })
    }
    watch(
      actions,
      (block, _, onCleanup) => {
        measureActing()
        if (!block || strip?.sheet !== true) return
        // The block's own attributes are ours — `docked` as it moves into the footer — and read again on
        // them a rule hiding the button in the footer alone let the footer go, took it back, and so on
        // for ever (round 4, Ж3). What the slot drew under it is read on every change.
        const observer = new MutationObserver((records) => {
          if (records.some((record) => record.type !== 'attributes' || record.target !== block)) {
            measureActing()
          }
        })
        observer.observe(block, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style', 'hidden', 'class'],
        })
        // A rule that starts or stops hiding a button changes nothing in the block: a stylesheet come or
        // gone, a media query as the phone turns (round 4, Ж1, Ж2).
        const styles = new MutationObserver(measureActing)
        styles.observe(document.head, { childList: true, subtree: true, characterData: true })
        window.addEventListener('resize', measureActing)
        onCleanup(() => {
          observer.disconnect()
          styles.disconnect()
          window.removeEventListener('resize', measureActing)
        })
      },
      { flush: 'post' },
    )
    watchEffect(() => {
      if (!strip) return
      const holds = props.kind === 'error' || (strip.sheet && acting.value)
      if (holds && !props.inline && !props.flow) strip.claim(me)
      else strip.release(me)
    })
    const dockTarget = computed(() => (strip?.owner.value === me ? strip.target.value : null))
    // A screen has one error of its own: a second one while the first holds the strip is drawn as a
    // section's, the quiet card — its own filled «Повторить» and «Обновить» were a second pair
    // (adversarial А4, 8c).
    const second = computed(
      () =>
        props.kind === 'error' &&
        strip?.owner.value != null &&
        strip.owner.value !== me &&
        !props.inline,
    )
    const card = computed(() => props.kind === 'error' && (props.inline || second.value))
    // In the flow the error offers «Обновить» under its words, where the keys leave it in sight, and
    // the strip's row of the version steps aside for it — one «Обновить», as when the strip is held
    // (8c, self-review №1).
    watchEffect(() => {
      if (!strip) return
      if (props.kind === 'error' && props.flow && !card.value) strip.offer(me)
      else strip.withdraw(me)
    })

    // Moved between the strip and the block — the error turned a section's or the screen's where it
    // stands — the buttons are other nodes or the same ones moved, and either way the focus fell to
    // the body (adversarial А2). It goes back to «Повторить», or to the first button of the block.
    watch(
      [dockTarget, card],
      () => {
        const focused = document.activeElement
        if (!(focused instanceof HTMLElement) || !actions.value?.contains(focused)) return
        const wasRetry = retryButton.value?.$el === focused
        void nextTick(() => {
          const retry = retryButton.value?.$el as unknown
          const next = wasRetry && retry instanceof HTMLElement ? retry : null
          ;(next ?? actions.value?.querySelector('button'))?.focus()
        })
      },
      { flush: 'pre' },
    )

    // The error offers «Обновить» in the row's place, and says the version out loud as the row did
    // (adversarial А3); a quiet card offers none — the row stands — and says nothing.
    useUpdateAnnouncement(() => props.kind === 'error' && !card.value)

    const glyph = computed<Component | undefined>(() => {
      if (props.kind === 'offline') return IconCloudOff
      if (props.kind === 'empty') return props.icon
      if (props.kind === 'attention') return IconAttention
      return IconAlert
    })

    // A tone the kind does not take is drawn as the kind's own, never as given: the validator
    // only warns, and a warning in the console must not turn offline red on the screen.
    const toneClass = computed(() => {
      if (props.kind === 'error') return 'bad'
      if (props.kind === 'empty') return 'quiet'
      if (props.kind !== 'offline') return 'warn'
      return props.tone !== undefined && TONES.offline.includes(props.tone) ? props.tone : FALLBACK
    })

    // An error on the screen interrupts: the person was waiting for an answer that did not
    // come. Everything else is polite — and so is anything inline: a notice is drawn again over
    // every screen the person moves to, and an alert would cut off the heading each move has
    // just focused (MOL-19, A3, Р-9). Polite words go to the app's live region when there is
    // one; outside the app the block carries `status` itself.
    const alerts = computed(
      () =>
        !props.inline && !second.value && (props.kind === 'error' || props.kind === 'attention'),
    )
    const role = computed(() => {
      if (alerts.value) return 'alert'
      return announce ? undefined : 'status'
    })

    // Words taken back when they are replaced or the block goes: the region must not keep
    // saying what is no longer on the screen.
    let withdraw: (() => void) | undefined
    function speak(): void {
      withdraw?.()
      withdraw = undefined
      if (alerts.value || !announce || props.silent) return
      withdraw = announce([props.title, props.body].filter(Boolean).join('. '), { held: true })
    }
    onMounted(speak)
    watch(() => [props.title, props.body, props.silent, props.attempt], speak)

    onBeforeUnmount(() => {
      withdraw?.()
      strip?.release(me)
      strip?.withdraw(me)
      // The buttons may stand in the strip, out of the block: a focus on them goes with them too. In a
      // sheet the sheet is looked for from the button, which may stand in its footer (MOL-317).
      const focused = document.activeElement
      if (root.value?.contains(focused) || actions.value?.contains(focused)) focusTitleOf(focused)
    })

    const update = usePwaUpdate()
    const updating = computed(() => ['ready', 'applying'].includes(update.phase.value))
    const applying = computed(() => update.phase.value === 'applying')

    // A block drawn outside the app — a component on its own, the kit — has no stores to ask.
    const pinia = getActivePinia()
    const actor = pinia ? useActorStore(pinia) : null
    const feedback = pinia ? useFeedbackSheetStore(pinia) : null
    const inDialog = ref(false)
    onMounted(() => {
      inDialog.value = root.value?.closest('dialog') != null
    })
    // The code is the one the error was shown with, not whatever failed by the tap: a person reads
    // the screen a while before writing, and another call may fail meanwhile (review №1).
    let shownAt = Date.now()
    watch(
      () => props.kind,
      (kind) => {
        if (kind === 'error') shownAt = Date.now()
      },
    )
    function report(): void {
      feedback?.open({ from: 'error', code: lastRefusal(shownAt) })
    }
    const reportable = computed(
      () =>
        props.kind === 'error' &&
        !card.value &&
        actor?.state === 'ready' &&
        feedback !== null &&
        !inDialog.value,
    )

    return {
      t,
      AppCard,
      setRoot,
      card,
      actions,
      retryButton,
      dockTarget,
      glyph,
      toneClass,
      role,
      update,
      updating,
      applying,
      reportable,
      report,
    }
  },
})
</script>

<style scoped lang="scss">
.state {
  @include appear;

  display: flex;
  flex: 1;
  flex-direction: column;
}

.inline,
.flow {
  flex: none;
}

.circle {
  display: grid;
  place-items: center;
  width: var(--state-circle);
  height: var(--state-circle);
  margin-bottom: var(--space-3);
  border-radius: 50%;
}

.glyph {
  @include icon;

  font-size: var(--state-glyph);
}

.quiet .circle {
  background: var(--surface-2);
  color: var(--text-muted);
}

.good .circle {
  background: var(--good-tint);
  color: var(--good-ink);
}

.warn .circle {
  background: var(--warn-tint);
  color: var(--warn-ink);
}

.bad .circle {
  background: var(--bad-tint);
  color: var(--bad-ink);
}

.title {
  margin: 0 0 var(--space-1);
  font-size: var(--text-headline);
  font-weight: var(--weight-medium);
  line-height: var(--leading-snug);
  overflow-wrap: anywhere;
}

.body {
  margin: 0;
  color: var(--text-muted);
  font-size: var(--text-footnote);
  line-height: var(--leading-body);
  overflow-wrap: anywhere;
}

.extra {
  margin-top: var(--space-2);
}

/* Pushed to the bottom of the free height: the thumb is there, not at the title. */
.action {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin-top: auto;
  padding-top: var(--space-4);
}

/* The quiet card's «Повторить»: the width of its word, the word in line with the text above. */
.card .action {
  align-items: flex-start;
  padding-top: var(--space-2);
}

.word {
  margin-left: calc(var(--space-4) * -1);
}

/* In the strip, whose margins and column are the frame's (К-9). */
.action.docked {
  margin-top: 0;
  padding-top: 0;
}

.inline .action,
.flow .action {
  margin-top: 0;
}

/* Side by side where they fit, so the second still stands over the keys (adversarial А5 of MOL-208):
   on the owner's iPhone the keys leave 395 px, and one under the other «Взять из недавних» stood 41 px
   below them. Each as wide as its word or more; a row that runs out wraps, and a lone one takes it whole —
   and shrinks, its words wrapping inside it, where a large text makes a word wider than the column
   (self-review №7: with no shrink «Сообщить о проблеме» stood past a screen of 320 at text ×1.5). */
.flow .action {
  flex-flow: row wrap;
  container-type: inline-size;

  > :deep(*) {
    flex: 1 1 auto;
    width: auto;
  }
}

/* Narrower than a pair with its sides — every iPhone of 375 in Russian, where «Повторить · Взять из
   недавних» needs some 346 px (adversarial А8) — the buttons keep 8 at their sides, not 16: the pair needs
   some 314 and stays side by side on 375 and 360. Not a basis of 0: a button shrinks below its word, and
   all three stood in one row with their words past the screen. */
@container (width < 22rem) {
  .flow .action > :deep(*) {
    padding-inline: var(--space-2);
  }
}
</style>
